import { Box, createTheme, ThemeProvider } from "@mui/material"
import { init } from "@noriginmedia/norigin-spatial-navigation"
import mixpanel from "mixpanel-browser"
import React, { useEffect, useMemo } from "react"
import { HashRouter as BrowserRouter, Route, Routes } from "react-router-dom"

import { BrowseScreen } from "./screens/Browse/BrowseScreen"
import { CategoryScreen } from "./screens/Category/CategoryScreen"
import { DetailScreen } from "./screens/Detail/DetailScreen"
import { FacetDetailScreen } from "./screens/FacetDetail/FacetDetailScreen"
import { HomeScreen } from "./screens/Home/HomeScreen"
import { LocaleSelectScreen } from "./screens/LocaleSelect/LocaleSelectScreen"
import { NotFound } from "./screens/NotFound"
import { OnboardingScreen } from "./screens/Onboarding/OnboardingScreen"
import { PlayerScreen } from "./screens/Player/PlayerScreen"
import { Root } from "./screens/Root"
import { appStore } from "./store/AppStore"
import "./App.css"
import { env, keydown, keyup, locale } from "./utilities/constants"

// focus manager
init({
  throttleKeypresses: true,
  throttle: 100,
  keyDownEventName: keydown,
  keyUpEventName: keyup,
  shouldFocusDOMNode: true,
  domNodeFocusOptions: { preventScroll: true },
})

export enum Pages {
  Root = "",
  LocaleSelect = "localeselect",
  Onboarding = "onboarding",
  Home = "home",
  MyStage = "MyStage",
  Settings = "settings",
  Browse = "browse/:type/:id/:page/:cursor",
  Player = "player/:type/:id/:isPreview/:trackId",
  Detail = "detail/:type/:id/:sliderIndex/:teaserIndex/:sliderId", // excessive params for tracking that were retrofitted to the app requirements
  Category = "category/:slug",
  FacetDetail = "facetdetail/:type/:id",
}

const mixpanelToken = env.mixpanelToken

export const App = () => {
  const tracking = appStore((state) => state.tracking)
  const currentUser = appStore((state) => state.currentUser)

  const theme = useMemo(
    () =>
      createTheme({
        typography: {
          fontFamily:
            locale === "ja" ? "Noto Sans Japanese" : "Lemon Sans Next",
        },
      }),
    [],
  )

  useEffect(() => {
    // init mixpanel
    if (tracking.mixpanel && mixpanelToken) {
      mixpanel.init(mixpanelToken, {
        debug: env.env === "staging",
        track_pageview: false,
        persistence: "localStorage",
      })
      if (currentUser?.id) mixpanel.identify(currentUser.id)
      else mixpanel.reset()
    }
  }, [tracking, currentUser])

  return (
    <ThemeProvider theme={theme}>
      <Box
        className={"App"}
        // disable mouse in playwright
        sx={{
          pointerEvents: process.env.REACT_APP_TEST ? "none" : undefined,
        }}
      >
        <BrowserRouter>
          <Routes>
            <Route path={Pages.Root} element={<Root />}>
              <Route
                path={Pages.LocaleSelect}
                element={<LocaleSelectScreen />}
              />
              <Route path={Pages.Onboarding} element={<OnboardingScreen />} />
              <Route path={Pages.Home} element={<HomeScreen />}>
                <Route path={Pages.Browse} element={<BrowseScreen />} />
                <Route path={Pages.Detail} element={<DetailScreen />} />
                <Route path={Pages.Category} element={<CategoryScreen />} />
                <Route
                  path={Pages.FacetDetail}
                  element={<FacetDetailScreen />}
                />
                <Route path="*" element={<NotFound />} />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
          <PlayerScreen />
        </BrowserRouter>
      </Box>
    </ThemeProvider>
  )
}
